import { logger } from '@springtree/eva-sdk-core-logger';
import { IEvaServiceCallOptions, bootstrapEndpoint } from '@springtree/eva-sdk-core-service';
import { IEvaServiceDefinition } from '@springtree/eva-services-core';
// tslint:disable-next-line: import-name
import ky from 'ky-universal';
import { IEvaReduxStoreDefaultShape } from '../store';
import { createLogic } from 'redux-logic';
import { IEvaServiceRequestAction, IStateOptions, IEvaServiceRequestActionPayload, IEvaServiceResponseActionPayload, IEvaServiceErrorActionPayload } from '../action';
import { getServicePromise } from '../promise';

/**
 * Factory method for generic EVA service logic
 *
 * @export
 * @template SVC
 * @param {new () => SVC} serviceDefinition The EVA Service registry definition
 * @param {string} [stateKey] Optional state key name if not the same as the service name
 * @returns {Logic<IEvaServiceState<SVC>, any, any, any, any, string>}
 */
export function createServiceRequestLogic<SHAPE extends IEvaReduxStoreDefaultShape, SVC extends IEvaServiceDefinition>(
  serviceDefinition: new () => SVC,
  stateOptions?: IStateOptions,
) {

  // Determine the state key name either by the service name or the provided
  // custom state key name
  //
  const serviceName = (stateOptions && stateOptions.stateKey) || new serviceDefinition().name;

  const requestLogic = createLogic<SHAPE, IEvaServiceRequestAction<SVC>, any, any, any, string>({
    type: `${serviceName}:REQUEST`,
    processOptions: {
      dispatchReturn: true,
      successType: `${serviceName}:RESPONSE`,
      failType: `${serviceName}:ERROR`,
    },
    async process({ getState, action }) {

      const storeState = getState();
      const payload: IEvaServiceRequestActionPayload<SVC> = action.payload;
      const endpointUrl = payload.endpoint || storeState.endpointUrl;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Ensure we have an endpoint to use
      //
      if (!endpointUrl) {
        const failure = new Error('No EVA endpoint provided for service call');
        servicePromise.reject(failure);
        throw failure;
      }

      // Bootstrapping the store to get the EvaEndpoint instance we need
      // The SDK caches bootstrap results so after the first call this will be instant
      //
      const endpoint = await bootstrapEndpoint({ uri: endpointUrl });

      // Set provided chain context header
      //
      const serviceOptions: IEvaServiceCallOptions = Object.assign(
        {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
        },
        payload.serviceOptions,
      );

      // Set the current user authentication token if needed
      //
      const currentUserToken = storeState.currentUserToken;
      if (!serviceOptions.authenticationToken && currentUserToken) {
        serviceOptions.authenticationToken = currentUserToken;
      }

      // Set the current application token if needed
      //
      const currentApplicationToken = storeState.currentApplicationToken;
      if (!serviceOptions.applicationToken && currentApplicationToken) {
        serviceOptions.applicationToken = currentApplicationToken;
      }

      // Call the service and return the response so the reducers can
      // write it to the store state
      //
      const serviceName = new serviceDefinition().name;
      logger.debug('[EVA:SDK:LOGIC] Call service', serviceName, payload, serviceOptions)();

      // Not logged in current user calls will be resolve as empty
      //
      if (serviceName === 'Core:GetCurrentUser' && !currentUserToken) {
        const responsePayload: IEvaServiceResponseActionPayload<SVC> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: undefined,
        };
        return responsePayload;
      }

      try {
        const result = await endpoint.callService(
          serviceDefinition,
          payload.data,
          serviceOptions,
        );
        const responsePayload: IEvaServiceResponseActionPayload<SVC> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: result,
        };

        return responsePayload;
      } catch (error) {
        logger.error('[EVA:SDK:LOGIC] Service error', error)();

        const errorPayload: IEvaServiceErrorActionPayload<SVC> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: error.message,
        };

        // If this an HTTPError from fetch we can try to extract the JSON response
        //
        if (error instanceof ky.HTTPError) {
          try {
            const httpError: ky.HTTPError = error;
            const jsonError = await httpError.response.json();
            errorPayload.data = jsonError || error.message;
            errorPayload.httpResponse = error.response.status;
            const headers: Record<string, string> = {};
            error.response.headers.forEach((value, key) => (headers[key.toLowerCase()] = value));
            errorPayload.headers = headers;
          } catch (jsonParseError) {
            logger.warn('[EVA:SDK:LOGIC] Failed to parse JSON error response', jsonParseError)();
          }
        }

        logger.error('[EVA:SDK:LOGIC] Error payload', errorPayload)();
        servicePromise.reject(errorPayload);
        throw errorPayload;
      }
    },
  });

  return [
    requestLogic,
  ];
}
